import { KEYBOARD_KEYS } from "@netapp/bxp-design-system-react";
import { createRef, forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { leftNav as i18nNamespace } from "../../../../../../i18n/namespace";
import leftNavStyles from "../CategoriesNav.module.scss";
import { CATEGORY_TYPES, CSS_CLASSES, TRANSLATION_KEYS } from "../constants/navigationConstants";
import { useCategoriesNavigation, useClickOutsideRef } from "../hooks";
import { useCategoryHandlers } from "../hooks/useCategoryHandlers";
import { useNavigationFocus } from "../hooks/useNavigationFocus";
import { useNavigationIndices } from "../hooks/useNavigationIndices";
import { selectCategoriesData, selectFavouritesData } from "../selectors/LeftNavSelectors";
import CategoryList from "./categories/CategoryList";
import Favourites from "./categories/FavouritesCategory";
import Home from "./categories/HomeCategory";
import Category from "./CategoryComponent";
import { useCreateCategoryRefs } from "../hooks/navigation/useCreateCategoryRefs";

const NavigationContainer = memo(forwardRef((props, forwardedRef) => {
    const [openCategory, setOpenCategory] = useState(null);
    const [selectedService, setSelectedService] = useState(null);

    const location = useLocation();
    const { t } = useTranslation(i18nNamespace);
    const { navItems, servicesByTo, activeFeatures, isHomePageEnabled } = useSelector(selectCategoriesData);
    const { favouritesList } = useSelector(selectFavouritesData);

    const categoryRefs = useCreateCategoryRefs(navItems);
    const favouritesRef = useRef(null);
    const homeRef = useRef(null);
    const lastNavRef = useRef(null);

    const { containerRef, handleContainerBlur } = useNavigationFocus(setOpenCategory);
    const { categoryOpenHandlers, favouritesOpenHandler, homeOpenHandler } = useCategoryHandlers(navItems, setOpenCategory);
    const navIndices = useNavigationIndices(isHomePageEnabled);
    const handleCategoryKeyDown = useCategoriesNavigation(isHomePageEnabled);

    const clickOutsideRef = useClickOutsideRef(setOpenCategory, openCategory !== null);
    const isAIDP = process.env.REACT_APP_IS_AIDP === "true";

    useEffect(() => {
        if (containerRef.current && clickOutsideRef.current !== containerRef.current) {
            clickOutsideRef.current = containerRef.current;
        }
    }, [containerRef, clickOutsideRef]);

    useImperativeHandle(forwardedRef, () => ({
        focusFirstNavItem: () => {
            // Focus on the first available element in order: Favourites -> Home -> First Category
            if (favouritesList.length > 0 && favouritesRef.current) {
                favouritesRef.current.focus();
            } else if (isHomePageEnabled && homeRef.current) {
                homeRef.current.focus();
            } else if (categoryRefs.current[0]?.current) {
                categoryRefs.current[0].current.focus();
            }
        }
    }), [isHomePageEnabled, favouritesList.length]);

    const activeCategory = useMemo(() => {
        if (!servicesByTo) {
            return;
        }

        const match = location.pathname.match(/(?<initialPath>\/[^/]+)/);
        if (!match?.groups?.initialPath) {
            return null;
        } else {
            let activeService = null;
            if (isAIDP) {
                for (const service of Object.keys(servicesByTo)) {
                    if (location.pathname.startsWith(service)) {
                        activeService = servicesByTo[service];
                        break;
                    }
                }
            } else {
                activeService = servicesByTo[match.groups.initialPath];
            }
            setSelectedService(activeService);
            return activeService?.category;
        }
    }, [location.pathname, servicesByTo, isAIDP]);

    // Auto-open the active category when left nav is shown and there's an active category
    useEffect(() => {
        if (props.isShowingCategoriesNav && activeCategory && navItems) {
            // Check if the active category exists in navItems and has services
            const activeCategoryData = navItems.find(category => category.label === activeCategory);
            if (activeCategoryData && activeCategoryData.services && activeCategoryData.services.length > 0) {
                // Only set the open category if it's not already the active one
                setOpenCategory(prevOpenCategory => {
                    if (prevOpenCategory !== activeCategory) {
                        return activeCategory;
                    }
                    return prevOpenCategory;
                });
            }
        }
    }, [props.isShowingCategoriesNav, activeCategory, navItems]);

    const executeHandleCategoryKeyDown = (e, currentIndex) => {
        const refs = {
            favouritesRef,
            homeRef,
            categoriesRefs: categoryRefs
        };
        handleCategoryKeyDown(e, currentIndex, refs);
    };

    const pressTabKeyBtnOnFocusHandler = useCallback((e) => {
        // Only immediately move focus if this wasn't triggered by skip navigation
        const isFromSkipNavigation = e.relatedTarget?.classList?.contains(CSS_CLASSES.SKIP_CONTENT);
        if (!isFromSkipNavigation && props.mainHeaderRef?.current?.focusMainLink) {
            props.mainHeaderRef.current.focusMainLink();
        }
    }, [props.mainHeaderRef]);

    const pressTabKeyBtnOnKeyDownHandler = useCallback((e) => {
        if (e.key === KEYBOARD_KEYS.TAB && !e.shiftKey && props.mainHeaderRef?.current?.focusMainLink) {
            e.preventDefault();
            props.mainHeaderRef.current.focusMainLink();
        }
    }, [props.mainHeaderRef]);

    return (!navItems ? <div /> : <>
        <div
            ref={containerRef}
            className={leftNavStyles.container}
            onBlur={handleContainerBlur}
        >
            {!isAIDP && <Favourites
                ref={favouritesRef}
                onCategoryMenuItemClicked={props.onCategoryMenuItemClicked}
                isOpen={openCategory === CATEGORY_TYPES.FAVORITES}
                open={favouritesOpenHandler}
                selectedService={selectedService}
                index={navIndices.favouritesIndex}
                onKeyDown={executeHandleCategoryKeyDown}
                className={leftNavStyles['nav-favourites']}
            />}
            {isHomePageEnabled && (
                <Home
                    ref={homeRef}
                    onCategoryMenuItemClicked={props.onCategoryMenuItemClicked}
                    isOpen={openCategory === CATEGORY_TYPES.HOME}
                    open={homeOpenHandler}
                    index={navIndices.homeIndex}
                    onKeyDown={executeHandleCategoryKeyDown}
                />
            )}
            <CategoryList
                navItems={navItems}
                activeFeatures={activeFeatures}
                isShowingCategoriesNav={props.isShowingCategoriesNav}
                openCategory={openCategory}
                activeCategory={activeCategory}
                categoryRefs={categoryRefs}
                categoryOpenHandlers={categoryOpenHandlers}
                selectedService={selectedService}
                navIndices={navIndices}
                onCategoryMenuItemClicked={props.onCategoryMenuItemClicked}
                executeHandleCategoryKeyDown={executeHandleCategoryKeyDown}
                CategoryComponent={Category}
            />
        </div>
        <button
            tabIndex={0}
            ref={lastNavRef}
            aria-label={t(TRANSLATION_KEYS.PRESS_TAB_KEY)}
            onFocus={pressTabKeyBtnOnFocusHandler}
            onKeyDown={pressTabKeyBtnOnKeyDownHandler}
        />
    </>);
}));

NavigationContainer.displayName = 'NavigationContainer';

export default NavigationContainer;
